import type {
  EligibilityAppointmentCalendarDateRange,
  EligibilityAppointmentDateRange,
} from "@/billing/types/eligibility-appointment-date-range";
import {
  addCalendarDays,
  getEndOfPracticeCalendarDate,
  getStartOfPracticeCalendarDate,
} from "@/shared/practice-timezone";

/** Number of calendar days in the default eligibility appointment window, counting today as day 1. */
export const ELIGIBILITY_DEFAULT_APPOINTMENT_WINDOW_DAYS = 14;

/** Maximum calendar days allowed for a custom eligibility appointment window, counting start as day 1. */
export const ELIGIBILITY_MAX_APPOINTMENT_WINDOW_DAYS = 90;

/**
 * Returns the default eligibility window: today through day 14 (today plus 13 days).
 *
 * @param today Practice-local calendar date (`yyyy-MM-dd`).
 */
export function getDefaultEligibilityAppointmentCalendarDateRange(
  today: string,
): EligibilityAppointmentCalendarDateRange {
  return {
    startCalendarDate: today,
    endCalendarDate: addCalendarDays(today, ELIGIBILITY_DEFAULT_APPOINTMENT_WINDOW_DAYS - 1),
  };
}

/** Returns true when the range matches the default next-two-weeks window for `today`. */
export function isDefaultEligibilityAppointmentCalendarDateRange(
  calendarDateRange: EligibilityAppointmentCalendarDateRange,
  today: string,
): boolean {
  const defaultRange = getDefaultEligibilityAppointmentCalendarDateRange(today);
  return (
    calendarDateRange.startCalendarDate === defaultRange.startCalendarDate &&
    calendarDateRange.endCalendarDate === defaultRange.endCalendarDate
  );
}

/**
 * Caps an eligibility window at the maximum allowed span.
 * Moves a past start date forward to today and an end date before the start up to the start.
 * When the span is too wide, keeps the start date and moves the end date earlier.
 *
 * @param calendarDateRange Requested start and end calendar dates (`yyyy-MM-dd`).
 * @param today Practice-local calendar date (`yyyy-MM-dd`).
 */
export function clampEligibilityAppointmentCalendarDateRange(
  calendarDateRange: EligibilityAppointmentCalendarDateRange,
  today: string,
): EligibilityAppointmentCalendarDateRange {
  const { startCalendarDate: requestedStartDate, endCalendarDate: requestedEndDate } =
    calendarDateRange;
  const startCalendarDate = requestedStartDate < today ? today : requestedStartDate;
  const maxEndCalendarDate = getEligibilityAppointmentDateRangeMaxEndDate(startCalendarDate);
  let endCalendarDate = requestedEndDate;
  if (endCalendarDate < startCalendarDate) {
    endCalendarDate = startCalendarDate;
  } else if (endCalendarDate > maxEndCalendarDate) {
    endCalendarDate = maxEndCalendarDate;
  }
  return { startCalendarDate, endCalendarDate };
}

/**
 * Returns the window the eligibility table shows for `today`.
 * Uses the default window when there is no custom range or the custom range ended before `today`.
 * Otherwise clamps the custom range, so it never starts in the past.
 *
 * @param customCalendarDateRange Picked calendar dates, or undefined for the default window.
 * @param today Practice-local calendar date (`yyyy-MM-dd`).
 */
export function resolveEligibilityAppointmentCalendarDateRange(
  customCalendarDateRange: EligibilityAppointmentCalendarDateRange | undefined,
  today: string,
): EligibilityAppointmentCalendarDateRange {
  if (!customCalendarDateRange || customCalendarDateRange.endCalendarDate < today) {
    return getDefaultEligibilityAppointmentCalendarDateRange(today);
  }
  return clampEligibilityAppointmentCalendarDateRange(customCalendarDateRange, today);
}

/** Latest selectable end calendar date (`yyyy-MM-dd`) for a custom range that starts on `startCalendarDate`. */
export function getEligibilityAppointmentDateRangeMaxEndDate(startCalendarDate: string): string {
  return addCalendarDays(startCalendarDate, ELIGIBILITY_MAX_APPOINTMENT_WINDOW_DAYS - 1);
}

/**
 * Converts a calendar-date window to instants in the practice timezone: practice-local midnight of
 * the start date through the last millisecond of the end date, so every appointment on those
 * calendar dates is inside the window for the whole day.
 *
 * @param calendarDateRange Window as calendar dates (`yyyy-MM-dd`).
 * @param timeZone IANA timezone of the practice.
 */
export function toEligibilityAppointmentDateRange(
  calendarDateRange: EligibilityAppointmentCalendarDateRange,
  timeZone: string,
): EligibilityAppointmentDateRange {
  return {
    startDate: getStartOfPracticeCalendarDate(calendarDateRange.startCalendarDate, timeZone),
    endDate: getEndOfPracticeCalendarDate(calendarDateRange.endCalendarDate, timeZone),
  };
}

/** Formats a calendar-date window as `MM/dd/yyyy – MM/dd/yyyy` for the Show control. */
export function formatEligibilityAppointmentDateRangeLabel(
  calendarDateRange: EligibilityAppointmentCalendarDateRange,
): string {
  return `${toUsDate(calendarDateRange.startCalendarDate)} – ${toUsDate(calendarDateRange.endCalendarDate)}`;
}

/** Rewrites a `yyyy-MM-dd` calendar date as `MM/dd/yyyy`. */
function toUsDate(calendarDate: string): string {
  const [year, month, day] = calendarDate.split("-");
  return `${month}/${day}/${year}`;
}
